import React from 'react';

import { useMutation } from '@apollo/react-hooks';

import AuthProvider from './AuthProvider';
import { getAuthToken, removeAuthToken, setAuthToken } from './utils';
import useNotifier from '../components/Messages/hooks';
import {
  authTokenMutation,
  registerUserMutation,
  verifyTokenMutation,
} from './mutations';
import { useNavigator } from '../components/Navigator';
import { isServer } from '../utils/helpers';

const initialState = {
  loading: true,
  user: null,
  club: null,
  errors: [],
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'setUser':
      return { ...state, user: action.user };
    case 'setClub':
      return { ...state, club: action.club };
    case 'setErrors':
      return { ...state, errors: action.errors };
    case 'setLoading':
      return { ...state, loading: action.loading };
    default:
      return { ...state };
  }
};

const AuthProviderContainer = ({ children }) => {
  const navigate = useNavigator();
  const pushMessage = useNotifier();
  const [state, dispatch] = React.useReducer(reducer, initialState);

  const handleVerify = (data) => {
    const { verifyToken } = data;
    if (verifyToken.ok && verifyToken.user) {
      dispatch({ type: 'setUser', user: verifyToken.user });
      dispatch({ type: 'setClub', club: verifyToken.club });
      dispatch({ type: 'setLoading', loading: false });
    } else {
      logout();
    }
  };

  const handleRegistration = (data) => {
    const { registerUser } = data;
    dispatch({ type: 'setLoading', loading: false });

    if (registerUser.ok) {
      setAuthToken(registerUser.token);
      dispatch({ type: 'setUser', user: registerUser.user });
      dispatch({ type: 'setClub', user: registerUser.club });
      navigate('/cabinet');
    } else {
      dispatch({ type: 'setErrors', errors: registerUser.errors });
    }
  };

  const handleAuth = (data) => {
    const { authToken } = data;

    if (authToken.ok && authToken.user) {
      setAuthToken(authToken.token);
      dispatch({ type: 'setUser', user: authToken.user });
      dispatch({ type: 'setClub', club: authToken.club });
      dispatch({ type: 'setLoading', loading: false });
      if (authToken.user.isAdmin) {
        navigate('/admin');
      } else {
        navigate('/cabinet');
      }
    } else {
      const authErrorMsg = <span>Can not find user</span>;
      pushMessage({ text: authErrorMsg });
    }
  };

  const [verifyToken] = useMutation(verifyTokenMutation, {
    onCompleted: handleVerify,
  });

  const [registerUser] = useMutation(registerUserMutation, {
    onCompleted: handleRegistration,
  });

  const [authToken] = useMutation(authTokenMutation, {
    onCompleted: handleAuth,
  });

  const login = (email, password) =>
    authToken({ variables: { email, password } });

  const verify = () => {
    const token = getAuthToken();
    if (!!token && !state.user) {
      return verifyToken({ variables: { token } });
    }
    dispatch({ type: 'setUser', user: null });
    dispatch({ type: 'setClub', club: null });
    dispatch({ type: 'setLoading', loading: false });
  };

  const register = (firstName, email, password) => {
    dispatch({ type: 'setErrors', errors: [] });
    return registerUser({
      variables: { inputData: { firstName, email, password } },
    });
  };

  const reset = () => {
    removeAuthToken();
    dispatch({ type: 'setUser', user: null });
    dispatch({ type: 'setClub', club: null });
  };

  const logout = () => {
    reset();
    navigate('/');
    if (!isServer()) {
      window.location.reload();
    }
  };

  return (
    <AuthProvider
      loading={state.loading}
      user={state.user}
      club={state.club}
      errors={state.errors}
      verify={verify}
      login={login}
      register={register}
      logout={logout}
    >
      {children}
    </AuthProvider>
  );
};

export default AuthProviderContainer;
